import {
  AdminApiError,
  type AdminClient,
  type RoundExplorerView,
} from '@apx/admin-sdk';
import { showToast } from '../toast.js';
import { askPrompt } from '../modal.js';
import { fmtTime } from '../format.js';
import { gameOptions, prettyGame } from '../games.js';

// Non-terminal states the orchestrator can be driving a round through. A round
// the admin might need to force-finish is any of these — IDLE (not started)
// and FINISHED (done) are excluded. The backend `incomplete` flag means
// `state != finished`, so we fetch that and drop IDLE client-side rather than
// firing one request per state.
const ACTIVE_STATES = new Set<string>([
  'betting_open',
  'betting_closing',
  'playing',
  'revealing',
  'settling',
  'showing_result',
]);

const STATE_PILL: Record<string, string> = {
  betting_open: 'live',
  betting_closing: 'warn',
  playing: '',
  revealing: '',
  settling: 'warn',
  showing_result: 'live',
};

export interface RoundOpsPageDeps {
  client: AdminClient;
  integratorId: string;
}

export async function renderRoundOpsPage(
  root: HTMLElement,
  deps: RoundOpsPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Round operations</h1>
    <p class="page-sub">Manual round start (kicks the orchestrator) + force-finish for any live round. Both are audited.</p>

    <div class="card">
      <h2>Manual start</h2>
      <div class="form-grid">
        <label>Game<select id="ro-game">${gameOptions('greedyking')}</select></label>
        <label>Scope key (optional)<input id="ro-scope" type="text" placeholder="e.g. liveHistoryId" /></label>
      </div>
      <div class="row">
        <button id="ro-start" class="primary">Start round</button>
        <span id="ro-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <div class="row" style="justify-content:space-between;align-items:flex-end;margin:0 0 12px">
        <div>
          <h2 style="margin:0">Active rounds</h2>
          <p class="muted" style="margin:4px 0 0">Any non-finished round (betting, mid-play, settling, showing result). Auto-refreshes every 5s.</p>
        </div>
        <div class="row" style="gap:10px;margin:0">
          <label style="margin:0">Game<select id="ro-filter">${gameOptions('', true)}</select></label>
          <span id="ro-meta" class="muted" style="font-size:12px"></span>
        </div>
      </div>
      <div id="ro-list"><p class="muted">Loading…</p></div>
    </div>
  `;

  const listEl = root.querySelector<HTMLElement>('#ro-list')!;
  const msgEl = root.querySelector<HTMLElement>('#ro-msg')!;
  const metaEl = root.querySelector<HTMLElement>('#ro-meta')!;
  const filterEl = root.querySelector<HTMLSelectElement>('#ro-filter')!;
  let timer: number | undefined;

  async function refresh(): Promise<void> {
    try {
      const gameFilter = filterEl.value || undefined;
      const res = await deps.client.listRounds(deps.integratorId, {
        incomplete: true,
        gameId: gameFilter,
        limit: 100,
      });
      const rounds = res.rounds
        .filter((r) => ACTIVE_STATES.has(r.state))
        .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
      listEl.innerHTML = renderTable(rounds);
      metaEl.textContent = `${rounds.length} active`;
      bindForceFinish();
    } catch (err) {
      listEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  function renderTable(rounds: RoundExplorerView[]): string {
    if (rounds.length === 0) return '<p class="muted">No active rounds.</p>';
    return `<div style="max-height:50vh;overflow:auto"><table>
      <thead><tr><th>Started</th><th>Game</th><th>State</th><th style="text-align:right">Bet</th><th style="text-align:right">Players</th><th>Round ID</th><th></th></tr></thead>
      <tbody>${rounds.map(rowHtml).join('')}</tbody>
    </table></div>`;
  }

  function rowHtml(r: RoundExplorerView): string {
    return `<tr>
      <td>${fmtTime(r.startedAt)}</td>
      <td><span class="pill" style="font-size:10px">${escapeHtml(prettyGame(r.gameId))}</span></td>
      <td><span class="pill ${STATE_PILL[r.state] ?? ''}">${escapeHtml(r.state)}</span></td>
      <td style="text-align:right;font-variant-numeric:tabular-nums">${Number(r.totalBet).toLocaleString()}</td>
      <td style="text-align:right;font-variant-numeric:tabular-nums">${r.participantCount}</td>
      <td><code>${r.id.slice(-12)}</code></td>
      <td><button data-finish="${r.id}" class="ghost">Force finish</button></td>
    </tr>`;
  }

  function bindForceFinish(): void {
    listEl.querySelectorAll<HTMLButtonElement>('button[data-finish]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const roundId = btn.dataset.finish!;
        const reason = await askPrompt('Reason for force-finish? (audit-logged)', 'manual');
        if (!reason) return;
        btn.disabled = true;
        try {
          await deps.client.forceFinishRound(roundId, reason);
          showToast(`Round ${roundId.slice(-8)} force-finished.`, 'success');
          await refresh();
        } catch (err) {
          btn.disabled = false;
          showToast(describe(err), 'error');
        }
      });
    });
  }

  root.querySelector<HTMLButtonElement>('#ro-start')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const gameId = root.querySelector<HTMLSelectElement>('#ro-game')!.value.trim();
    const scopeKey = root.querySelector<HTMLInputElement>('#ro-scope')!.value.trim() || undefined;
    if (!gameId) {
      msgEl.textContent = 'game id required';
      msgEl.className = 'error';
      return;
    }
    try {
      const round = await deps.client.startManualRound({
        gameId,
        integratorId: deps.integratorId,
        scopeKey,
      });
      msgEl.textContent = `Started ${round.id} (${round.state}).`;
      msgEl.className = 'muted';
      await refresh();
    } catch (err) {
      msgEl.textContent = describe(err);
      msgEl.className = 'error';
    }
  });

  filterEl.addEventListener('change', () => void refresh());

  const observer = new MutationObserver(() => {
    if (!document.body.contains(root)) {
      if (timer !== undefined) window.clearInterval(timer);
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  await refresh();
  timer = window.setInterval(() => void refresh(), 5_000);
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
